/*
  Combo Collection — assets/combo-collection.js
  Complete file. Replaces everything previously in this asset.

  Contains: pill switching, the native <dialog> preview, and an AJAX
  add-to-cart that hands off to the theme's own cart drawer instead of
  letting the browser redirect to /cart.

  Everything is delegated off document, so nothing needs re-initialising
  when the section is re-rendered in the theme editor.
*/
(function () {
  'use strict';

  /* -------------------------------------------------------------- pills --- */

  function pillsOf(card) {
    return Array.prototype.slice.call(card.querySelectorAll('[data-cc-pill]'));
  }

  function selectPill(card, pill) {
    var index = pill.getAttribute('data-cc-pill');

    pillsOf(card).forEach(function (p) {
      var on = p === pill;
      p.classList.toggle('is-active', on);
      p.setAttribute('aria-selected', on ? 'true' : 'false');
      p.setAttribute('tabindex', on ? '0' : '-1');
    });

    card.querySelectorAll('[data-cc-detail]').forEach(function (detail) {
      detail.classList.toggle('is-active', detail.getAttribute('data-cc-detail') === index);
    });

    // Variant pills also swap the price and what gets added to the cart.
    var variantId = pill.getAttribute('data-variant-id');
    if (!variantId) return;

    var input = card.querySelector('[data-cc-variant-input]');
    if (input) input.value = variantId;

    var now = card.querySelector('[data-cc-now]');
    if (now && pill.getAttribute('data-price')) {
      now.textContent = pill.getAttribute('data-price');
    }

    var compareEl = card.querySelector('[data-cc-compare]');
    if (compareEl) {
      var compare = pill.getAttribute('data-compare');
      if (compare) {
        compareEl.textContent = compare;
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.setAttribute('hidden', '');
      }
    }

    var button = card.querySelector('.cc-button');
    var label = card.querySelector('[data-cc-add-text]');
    if (button) {
      var available = pill.getAttribute('data-available') !== 'false';
      button.disabled = !available;
      if (label && !available) {
        if (!label.dataset.ccDefault) label.dataset.ccDefault = label.textContent.trim();
        label.textContent = 'Sold out';
      } else if (label && label.dataset.ccDefault) {
        label.textContent = label.dataset.ccDefault;
      }
    }
  }

  /* ------------------------------------------------------------ preview --- */

  function openPreview(trigger) {
    var dialog = document.getElementById(trigger.getAttribute('data-cc-preview-open'));
    if (!dialog) return;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', ''); // very old browsers
    }
    document.body.classList.add('cc-preview-open');
  }

  function closePreview(dialog) {
    if (!dialog) return;

    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    document.body.classList.remove('cc-preview-open');
  }

  /* --------------------------------------------------------- cart drawer --- */

  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  // Theme namespaces used by FoxEcom builds and common OS 2.0 themes.
  var NAMESPACES = ['FoxTheme', 'SleekTheme', 'MinimogTheme', 'Minimog', 'theme', 'Theme'];

  function drawerElement() {
    return document.querySelector(
      'cart-drawer, #CartDrawer, #cart-drawer, .cart-drawer, [data-cart-drawer], #shopify-section-cart-drawer'
    );
  }

  // Re-render the drawer and the header cart count through the Section
  // Rendering API. Silently skipped if those sections are not on the page.
  function refreshCartSections() {
    var ids = ['cart-drawer', 'cart-icon-bubble', 'cart-bubble', 'cart-notification', 'cart-count']
      .filter(function (id) { return document.getElementById('shopify-section-' + id); });

    if (!ids.length) return Promise.resolve();

    return fetch(ROOT + '?sections=' + ids.join(','))
      .then(function (res) { return res.json(); })
      .then(function (sections) {
        ids.forEach(function (id) {
          var html = sections[id];
          var target = document.getElementById('shopify-section-' + id);
          if (!html || !target) return;

          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.getElementById('shopify-section-' + id) || parsed.body.firstElementChild;
          if (fresh) target.innerHTML = fresh.innerHTML;
        });
      })
      .catch(function () { /* drawer will still open with its own fetch */ });
  }

  function publishCartUpdate(cart) {
    var detail = { cart: cart, source: 'combo-collection' };

    NAMESPACES.forEach(function (name) {
      var ns = window[name];
      if (!ns) return;

      if (ns.pubsub && typeof ns.pubsub.publish === 'function') {
        var evt = (ns.pubsub.PUB_SUB_EVENTS && ns.pubsub.PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
        try { ns.pubsub.publish(evt, detail); } catch (e) {}
      }
      if (ns.EventBus && typeof ns.EventBus.emit === 'function') {
        try { ns.EventBus.emit('cart:updated', detail); } catch (e) {}
      }
    });

    ['cart:refresh', 'cart:updated', 'cart:build', 'cart:change'].forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    });
  }

  function openCartDrawer(cart) {
    // 1. Theme API, if the build exposes one.
    for (var i = 0; i < NAMESPACES.length; i++) {
      var ns = window[NAMESPACES[i]];
      if (!ns) continue;
      var api = ns.CartDrawer || ns.Cart || ns.cartDrawer;
      if (api) {
        if (typeof api.open === 'function') { api.open(); return true; }
        if (typeof api.show === 'function') { api.show(); return true; }
        if (typeof api.renderContents === 'function') { api.renderContents({ cart: cart }); return true; }
      }
    }

    // 2. Methods on the drawer custom element itself.
    var drawer = drawerElement();
    if (drawer) {
      if (typeof drawer.open === 'function') { drawer.open(); return true; }
      if (typeof drawer.show === 'function') { drawer.show(); return true; }
      if (typeof drawer.renderContents === 'function') { drawer.renderContents({ cart: cart }); return true; }
      if (typeof drawer.showModal === 'function') { drawer.showModal(); return true; }
    }

    // 3. The theme's own cart toggle in the header.
    var toggle = document.querySelector(
      '[data-cart-drawer-toggle], [data-drawer-target="cart"], .js-drawer-open-cart, ' +
      'a[href$="/cart"][aria-controls], .header__icon--cart, [data-action="open-cart"]'
    );
    if (toggle) { toggle.click(); return true; }

    return false;
  }

  /* -------------------------------------------------------- add to cart --- */

  function addToCart(form) {
    var button = form.querySelector('.cc-button');
    var label = form.querySelector('[data-cc-add-text]');
    var original = label ? label.textContent.trim() : '';
    var cartJson = null;

    if (button) button.disabled = true;
    if (label) label.textContent = 'Adding…';

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw new Error(json.description || json.message || 'Add to cart failed');
          return json;
        });
      })
      .then(function () { return fetch(ROOT + 'cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        cartJson = cart;
        return refreshCartSections();
      })
      .then(function () {
        publishCartUpdate(cartJson);
        if (!openCartDrawer(cartJson)) {
          // No drawer found on this page — keep the old behaviour rather
          // than leaving the shopper with no feedback at all.
          window.location.href = ROOT + 'cart';
          return;
        }
        if (label) label.textContent = original;
        if (button) button.disabled = false;
      })
      .catch(function (err) {
        console.error('[combo-collection] add to cart:', err);
        if (label) label.textContent = 'Try again';
        if (button) button.disabled = false;
        setTimeout(function () { if (label) label.textContent = original; }, 2000);
      });
  }

  /* ------------------------------------------------------------- events --- */

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('form.cc-form, form[data-cc-form]');
    if (!form) return;
    event.preventDefault();
    event.stopPropagation();
    addToCart(form);
  }, true);

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    // Clicking the dialog element itself means the backdrop was hit.
    if (event.target.matches && event.target.matches('.cc-preview[open]')) {
      closePreview(event.target);
      return;
    }

    var closer = event.target.closest('[data-cc-preview-close]');
    if (closer) {
      event.preventDefault();
      closePreview(closer.closest('.cc-preview'));
      return;
    }

    var more = event.target.closest('[data-cc-preview-open]');
    if (more) {
      event.preventDefault();
      openPreview(more);
      return;
    }

    var pill = event.target.closest('[data-cc-pill]');
    if (pill) {
      var card = pill.closest('[data-product-card]');
      if (card) {
        event.preventDefault();
        selectPill(card, pill);
      }
    }
  });

  // Esc is handled natively by <dialog>; just clear the scroll lock.
  document.addEventListener('close', function (event) {
    if (event.target.classList && event.target.classList.contains('cc-preview')) {
      document.body.classList.remove('cc-preview-open');
    }
  }, true);

  document.addEventListener('keydown', function (event) {
    if (!event.target.closest) return;

    var pill = event.target.closest('[data-cc-pill]');
    if (!pill) return;

    var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (keys.indexOf(event.key) === -1) return;

    var card = pill.closest('[data-product-card]');
    if (!card) return;

    var pills = pillsOf(card);
    var i = pills.indexOf(pill);
    var next = i;

    if (event.key === 'ArrowLeft') next = (i - 1 + pills.length) % pills.length;
    if (event.key === 'ArrowRight') next = (i + 1) % pills.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = pills.length - 1;

    event.preventDefault();
    selectPill(card, pills[next]);
    pills[next].focus();
  });

  /* -------------------------------------------------------------- debug --- */
  // Run ccCartDebug() in the console on the combo page and send me the output
  // if the drawer still doesn't open.
  window.ccCartDebug = function () {
    var found = NAMESPACES.filter(function (n) { return !!window[n]; });
    var drawer = drawerElement();
    console.log('theme namespaces:', found);
    found.forEach(function (n) { console.log(n, '→', Object.keys(window[n])); });
    console.log('drawer element:', drawer);
    if (drawer) {
      console.log('drawer tag:', drawer.tagName);
      console.log('drawer methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(drawer)));
    }
    console.log('cart sections present:', ['cart-drawer', 'cart-icon-bubble', 'cart-notification']
      .filter(function (id) { return !!document.getElementById('shopify-section-' + id); }));
  };
})();